import { Pause, Play, Radio } from "lucide-react";
import { useEffect, useState } from "react";

import { CVLink } from "@/components/cv-link";
import { Image } from "@/components/image";
import type { OnAirEpisode } from "@/lib/episodes";
import { episodeProgress } from "@/lib/episodes";
import type { ResultData } from "@/lib/metadata";
import { cn, formatTimeToHHMM } from "@/lib/utils";

function trackTitle(metadata: ResultData | null) {
  const title = metadata?.track.title;

  if (!title || title === "By" || title.length <= 2) {
    return null;
  }

  return title;
}

export function NowPlayingCard(props: {
  episode: OnAirEpisode | null;
  metadata: ResultData | null;
  playing: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  const { episode } = props;
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 10_000);

    return () => clearInterval(id);
  }, []);

  const progress = episode ? episodeProgress(episode) : 0;

  const track = trackTitle(props.metadata);
  const heading = episode?.title ?? track ?? "Off air";
  const subheading = episode ? (
    <>
      <CVLink text={episode.show.name} url={`/view/shows/${episode.show.id}`} />
      {episode.hosts.length > 0 && (
        <>
          {" with "}
          {episode.hosts.map((host, i) => (
            <span key={host.id}>
              {i > 0 && (i === episode.hosts.length - 1 ? " and " : ", ")}
              <CVLink text={host.firstName} url={`/view/people/${host.id}`} />
            </span>
          ))}
        </>
      )}
    </>
  ) : (
    <>
      Check the{" "}
      <a
        className="text-primary font-medium underline-offset-4 hover:underline"
        href="#schedule"
      >
        schedule
      </a>{" "}
      to see when we&rsquo;re next on.
    </>
  );

  return (
    <div className="bg-card text-card-foreground rounded-3xl p-6 sm:p-8 shadow-lg shadow-black/10">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <button
          aria-label={props.playing ? "Pause" : "Listen live"}
          className={cn(
            "bg-primary text-primary-foreground size-20 shrink-0 rounded-full",
            "inline-flex items-center justify-center",
            "shadow-sm transition-[background-color,transform,box-shadow] duration-150",
            "hover:bg-primary/90 active:scale-[0.97]",
            "focus-visible:ring-primary/40 outline-none focus-visible:ring-4",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          disabled={props.disabled}
          onClick={props.onToggle}
          type="button"
        >
          {props.playing ? (
            <Pause className="size-8 fill-current" />
          ) : (
            <Play className="size-8 translate-x-0.5 fill-current" />
          )}
        </button>

        {episode && (
          <div className="relative hidden size-32 shrink-0 self-start overflow-hidden rounded-2xl bg-primary/10 sm:block">
            {episode.iconUrl ? (
              <Image
                fill
                alt={`${episode.show.name} artwork`}
                className="object-cover"
                src={episode.iconUrl}
              />
            ) : (
              <span className="text-primary flex size-full items-center justify-center">
                <Radio className="size-10" />
              </span>
            )}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <h1 className="text-4xl font-extrabold tracking-tight text-balance sm:text-5xl">
            {heading}
          </h1>
          <p className="text-muted-foreground mt-3 text-lg">{subheading}</p>

          {episode?.show.description && (
            <p className="text-muted-foreground mt-3 line-clamp-2 max-w-prose">
              {episode.show.description}
            </p>
          )}

          {episode && (
            <div className="mt-6">
              <progress
                aria-label="Episode progress"
                className="bg-primary/15 h-1.5 w-full appearance-none overflow-hidden rounded-full [&::-moz-progress-bar]:bg-primary [&::-webkit-progress-bar]:bg-primary/15 [&::-webkit-progress-value]:bg-primary"
                max={1}
                value={progress}
              />
              <p className="text-muted-foreground mt-2 text-sm tabular-nums">
                {formatTimeToHHMM(new Date(episode.startOfEpisode))}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
